import DownSvg from 'assets/svg/Down';
import Legend from 'components/molecules/Legend';
import { CustomSelect } from 'components/pages/GrowthAnalysis';
import { salesTileItemType } from 'components/pages/CommercialForecast';
import { height, subHeaderClass, yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import React, { useContext, useMemo, useState } from 'react';
import {
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import { barWidth } from './ForecastChartContainer';
import Barchart from 'components/molecules/Barchart';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import Linechart from 'components/molecules/Linechart';
import AreaBarChart from 'components/molecules/AreaBarChart';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { FilterContextData } from 'contexts/FilterContext';
import { commercialForecastUrls } from 'config/urls';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import dayjs from 'dayjs';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { salesGraphWidth } from '../PVMAnalysis/PVMInfiniteImpactChartContainer';

type Props = {
    expandedItem: any;
    salesTile: salesTileItemType;
    loading: boolean;
    stringifiedFilter: string;
};

const containerId = 'commercial-forecast-trend-chart-container';

const CFTrendBarChartContainer = ({ expandedItem, salesTile, loading, stringifiedFilter }: Props) => {
    const { appConfig } = useContext(UserContext);

    const { timePeriodSelection, allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);
    const commercialForecastConfig = getConfig('commercialForecast', appConfig);
    const trendChartDropdownData = commercialForecastConfig.trendChartDropdownData;
    const externalAnalyticsToolName = commercialForecastConfig.externalAnalyticsToolName;

    const [dropdownSelectedData, setDropdownSelectedData] = useState(trendChartDropdownData?.[0]);
    const commonConfig = getConfig('commonConfig', appConfig);
    const variables = {
        time_period: JSON.parse(timePeriodSelection || '{}'),
        filters: JSON.parse(stringifiedFilter || '{}'),
        data_type: salesTile.key,
    };

    //commercial forecast data chart 1
    const {
        data: trendsData,
        loading: trendsDataLoading,
        error: trendsDataError,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: !loading && Boolean(expandedItem) },
        queryKey: ['commercial-forecast-trend-chart', variables],
        url: `${commercialForecastUrls.trendGraphUrl}`,
        variables: {
            ...variables,
        },
        parserFunction: (res: any[]) => {
            let minVal = 0,
                maxVal = 0;
            const result = res?.map((item: any) => {
                const asper = item?.asper;
                const external = item?.external;
                const difference = item?.difference;
                minVal = Math.min(minVal, asper, external, difference);
                maxVal = Math.max(maxVal, asper, external, difference);
                return {
                    ...item,
                    color: difference > 0 ? colorConstants.limeGreen : colorConstants.red,
                    date: dayjs(item?.date).format(commonConfig.dateFormatAlt),
                };
            });
            return { minVal, maxVal, data: result };
        },
    });
    //used to debounce Loading state, so that UI wont flicker
    const { loading: combinedLoading } = useDebounceLoading({
        loadingDependency: loading || trendsDataLoading,
    });
    const containerWidth = useContainerWidth({
        containerId: containerId,
        buffer: yAxisGraphWidth + 34 * 2,
    }); //subtracting graph width and padding on 2 sides

    const graphViewContainerWidth = Math.max(
        trendsData?.data ? trendsData.data?.length * salesGraphWidth : 0,
        containerWidth,
    );

    const legendsData = useMemo(() => {
        return [
            { label: `${salesTile.label} Units Asper`, color: colorConstants.pink },
            {
                label: `${salesTile.label} Units ${externalAnalyticsToolName?.shortName}`,
                color: colorConstants.lightBlue,
            },
            { label: `Positive Difference`, color: colorConstants.limeGreen },
            { label: `Negative Difference`, color: colorConstants.red },
        ];
    }, [salesTile.key]);

    const subHeader = `Units - ${expandedItem?.attributeExId}`;
    const currency = fetchCurrency(appConfig);

    // IF ERROR RETURN ERROR MESSAGE
    if (trendsDataError && !combinedLoading && !trendsData) return <ErrorBox msg={String(trendsDataError)} />;

    // IF LOADING RETURN LOADER
    if (combinedLoading)
        return (
            <div style={{ height: 350, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );
    return (
        <section>
            <div className="flex items-center justify-between mb-4">
                <p className={subHeaderClass}> {subHeader}</p>
                {/* <div className="flex justify-between items-center">
                    <CustomSelect
                        suffixIcon={<DownSvg />}
                        disabled={false}
                        //loading={allDimAndAttributeTypeList?.loading}
                        value={dropdownSelectedData.key}
                        options={trendChartDropdownData}
                        className="dropdown-height dropdown-width-alt uniqSelGrowthAnalysisDropdown"
                        onChange={(val: any, item: any) => {
                            setDropdownSelectedData(item);
                        }}
                    />
                </div> */}
            </div>
            <div className="flex justify-between">
                <p className={yAxisLabelClass}> Units</p>
                <Legend data={legendsData} />
                <p className={`${yAxisLabelClass} w-[60px]`}></p>
            </div>
            <div id="commercialForecastAreaTrendsChart" className="flex ml-1 w-full mt-2">
                <div style={{ minWidth: yAxisGraphWidth }}>
                    {/* Y AXIS */}
                    <Barchart
                        colorKey="color"
                        dataset={[]}
                        height={height}
                        loading={false}
                        chartContainer={`forecast-trends-chart-y`}
                        fields={['category', 'sales']}
                        minBound={scaleMinValue(105, trendsData?.minVal)}
                        maxBound={scaleMaxValue(80, trendsData?.maxVal)}
                        width={yAxisGraphWidth}
                        YGrid={false}
                        padding={[0, 0, 14, 0]}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMB(val);
                        }}
                        xAxisConfig={false}
                    />
                </div>
                {/* MAIN CONTAINER */}
                <div
                    id={containerId}
                    className="flex pb-[45px]"
                    style={{ maxWidth: '100%', overflowX: 'auto', height: height + 25 }} //giving extra height, so that x axis label is shown properly without a scrollbar
                >
                    <AreaBarChart
                        barWidth={barWidth}
                        type="bar"
                        intervalLabelTooltipKey="difference"
                        width={graphViewContainerWidth}
                        range={[0.001, 0.98]}
                        dataset={trendsData?.data}
                        minBound={scaleMinValue(105, trendsData?.minVal)}
                        maxBound={scaleMaxValue(80, trendsData?.maxVal)}
                        chartContainer="chart-container-depletion"
                        height={height}
                        loading={false}
                        fields={['date', 'difference']}
                        color={colorConstants.lightBlueAlt}
                        xAxisLabelFormatter={(val: any) => {
                            return val;
                        }}
                        intervalFormatter={(val) => {
                            return formatNumWithKMBAlt(val, 2, 2);
                        }}
                        XGrid={false}
                        disableIntervalLabel
                        tooltipFormatter={(xAxis: any, val: any) => {
                            const label = val > 0 ? legendsData?.[2]?.label : legendsData?.[3]?.label;
                            return {
                                name: label,
                                value: formatNumWithKMBAlt(val, 2, 2),
                            };
                        }}
                        groupedChartColor={[colorConstants.pink, colorConstants.lightBlue]}
                        lineChartParams={{
                            field1: 'asper',
                            field2: 'external',
                            minBound: scaleMinValue(105, trendsData?.minVal),
                            maxBound: scaleMaxValue(80, trendsData?.maxVal),
                            tooltipFormatter: (xAxis: any, val: any) => {
                                const label = xAxis === 'asper' ? legendsData?.[0]?.label : legendsData?.[1]?.label;
                                return {
                                    name: label,
                                    value: formatNumWithKMBAlt(val, 2, 2),
                                };
                            },
                        }}
                    />
                </div>
            </div>
        </section>
    );
};

export default CFTrendBarChartContainer;
